import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { S3AccessPolicySchema } from "../../../../../utils/validations";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { useCallback, useEffect, useState } from "react";
import {
  selectBucketDetails,
  handleHideAccessPolictEditor,
  selectShowAccessPolicyEditor,
  selectBucketAccessPolicyLoading,
  setBucketAccessPolicyAsync,
  getBucketDetailsAsync,
} from "../../../../../store/s3/buckets/bucketSlice";
import { AccessPolicyTypes } from "../../../../../types/s3-bucket";
import { Button, Modal, Select, SelectOption, Textarea } from "djuno-design";

export const accessPolicyOptions: SelectOption[] = [
  { label: "Public", value: "PUBLIC" },
  { label: "Private", value: "PRIVATE" },
  { label: "Custom", value: "CUSTOM" },
];

const S3AccessPolicyEditorModal = () => {
  const isOpen = useAppSelector(selectShowAccessPolicyEditor);
  const loading = useAppSelector(selectBucketAccessPolicyLoading);
  const bucketDetails = useAppSelector(selectBucketDetails);
  const [selectedAccessPolicy, setSelectedAccessPolicy] = useState<
    string | undefined
  >();
  const dispatch = useAppDispatch();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(
      S3AccessPolicySchema(
        selectedAccessPolicy as AccessPolicyTypes | undefined
      )
    ),
  });

  //set form data with bucket details
  useEffect(() => {
    if (isOpen && bucketDetails) {
      if (typeof bucketDetails.definition !== "undefined") {
        setValue("access", bucketDetails.access);
        setSelectedAccessPolicy(bucketDetails.access);
      }
      setValue("definition", bucketDetails.definition);
    } else {
      reset();
    }
  }, [isOpen, bucketDetails, setValue, reset]);

  const handleClearComponent = useCallback(() => {
    setSelectedAccessPolicy(undefined);
    reset();
  }, [reset]);

  const onSubmit = (data: any) => {
    // data:
    //definition -> string or ''
    //--- others:
    // selectedAccessPolicy
    console.log(data);
    if (bucketDetails && selectedAccessPolicy) {
      dispatch(
        setBucketAccessPolicyAsync({
          name: bucketDetails.name,
          formData: {
            access: selectedAccessPolicy as AccessPolicyTypes,
            definition: data.definition || bucketDetails.definition,
          },
        })
      ).then((action) => {
        if (action.type === "bucket/access-policy/set/fulfilled") {
          handleClearComponent();
          dispatch(handleHideAccessPolictEditor());
          dispatch(getBucketDetailsAsync({ name: bucketDetails.name }));
        }
      });
    }
  };

  const handleOnClose = useCallback(() => {
    dispatch(handleHideAccessPolictEditor());
    handleClearComponent();
  }, [dispatch, handleClearComponent]);

  useEffect(() => {
    return () => {
      handleOnClose();
    };
  }, [handleOnClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleOnClose}
      contentClassName="max-w-lg"
      title={"Change Access Policy"}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mt-5 flex flex-col w-full gap-5">
          <Select
            label="Access Policy"
            options={accessPolicyOptions}
            value={selectedAccessPolicy}
            onChange={(o) => {
              setValue("access", o ? o : "");
              setSelectedAccessPolicy(o);
            }}
            error={errors.access?.message}
          />
          {selectedAccessPolicy === "CUSTOM" && (
            <Textarea
              {...register("definition")}
              label="Write Policy"
              error={errors.definition?.message}
              rows={4}
            />
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button
            onClick={(e) => {
              e.preventDefault();
              dispatch(handleHideAccessPolictEditor());
            }}
            uiType="light"
          >
            Cancel
          </Button>
          <Button
            loading={loading}
            uiType="primary"
            disabled={loading}
            type="submit"
          >
            Set
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default S3AccessPolicyEditorModal;
